import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListChecks } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import VerificationItemTable from '../VerificationItemTable';
import {
  VerificationSession,
  VerificationUnit,
  VerificationItem,
} from '../../../types/verification';

interface VerificationSessionDetailProps {
  isLoading: boolean;
  session: VerificationSession | null;
  onAddUnit: () => void;
  onEditUnit: (unit: VerificationUnit) => void;
  onDeleteUnit: (unitId: string) => void;
  onAddItem: (unitId: string) => void;
  onEditItem: (item: VerificationItem) => void;
  onDeleteItem: (itemId: string) => void;
}

const VerificationSessionDetail: React.FC<VerificationSessionDetailProps> = ({
  isLoading,
  session,
  onAddUnit,
  onEditUnit,
  onDeleteUnit,
  onAddItem,
  onEditItem,
  onDeleteItem,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex justify-center pt-16">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex flex-col items-center pt-16">
        <ListChecks
          className="mb-4 size-16 text-muted-foreground"
          strokeWidth={1.2}
          aria-hidden="true"
        />
        <p className="revy-cjk leading-[1.5] text-muted-foreground">
          {t('verification.selectSession')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="revy-cjk text-base font-medium leading-[1.5]">{session.title}</h2>
        {session.modelName && (
          <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
            {t('verification.modelName')}: {session.modelName}
          </p>
        )}
        {session.description && (
          <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
            {session.description}
          </p>
        )}
      </div>

      <VerificationItemTable
        session={session}
        onAddUnit={onAddUnit}
        onEditUnit={onEditUnit}
        onDeleteUnit={onDeleteUnit}
        onAddItem={onAddItem}
        onEditItem={onEditItem}
        onDeleteItem={onDeleteItem}
      />
    </div>
  );
};

export default VerificationSessionDetail;
